import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Language options with flags
export const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧', nativeName: 'English' },
  { code: 'ms', name: 'Bahasa Malaysia', flag: '🇲🇾', nativeName: 'Bahasa Melayu' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳', nativeName: '中文' },
  { code: 'ta', name: 'Tamil', flag: '🇮🇳', nativeName: 'தமிழ்' }
];

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('hrms_language') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('hrms_language', language);
  }, [language]);

  const changeLanguage = (langCode) => {
    if (languages.find(l => l.code === langCode)) {
      setLanguage(langCode);
    }
  };

  const toggleLanguage = () => {
    const currentIndex = languages.findIndex(l => l.code === language);
    const nextIndex = (currentIndex + 1) % languages.length;
    setLanguage(languages[nextIndex].code);
  };

  const getCurrentLanguage = () => {
    return languages.find(l => l.code === language) || languages[0];
  };

  const t = (key) => {
    const translations = {
      en: {
        // Landing Page - Header
        'landing.nav.features': 'Features',
        'landing.nav.pricing': 'Pricing',
        'landing.nav.contact': 'Contact Sales',
        'landing.nav.signIn': 'Sign In',
        'landing.nav.getStarted': 'Get Started Free',
        
        // Landing Page - Hero
        'landing.hero.badge': 'AI-Powered HRMS for Malaysian Businesses',
        'landing.hero.title1': 'The Future of HR is',
        'landing.hero.title2': 'Intelligent & Automated',
        'landing.hero.subtitle': 'Transform your HR operations with Malaysia\'s most advanced AI-powered HRMS. Automate payroll, streamline leave management, ensure 100% statutory compliance, and unlock powerful workforce analytics — all in one intelligent platform.',
        'landing.hero.bookDemo': 'Book a Free Demo',
        'landing.hero.startTrial': 'Start 14-Day Free Trial',
        'landing.hero.stat1': 'Employees Managed',
        'landing.hero.stat2': 'Companies Trust Us',
        'landing.hero.stat3': 'Uptime Guarantee',
        'landing.hero.stat4': 'Saved Monthly',
        
        // Landing Page - Benefits
        'landing.benefits.compliance': '100% Malaysian Compliance',
        'landing.benefits.cloud': 'Cloud-Based Access Anywhere',
        'landing.benefits.security': 'Enterprise-Grade Security',
        'landing.benefits.ai': 'AI-Powered Automation',
        'landing.benefits.setup': '5-Minute Setup',
        'landing.benefits.support': '24/7 Premium Support',
        
        // Landing Page - Features
        'landing.features.badge': 'AI-Powered Features',
        'landing.features.title': 'Intelligent HR Automation',
        'landing.features.subtitle': 'Leverage cutting-edge AI and machine learning to automate complex HR processes, reduce manual errors, and make data-driven workforce decisions.',
        'landing.features.payroll': 'AI-Powered Payroll',
        'landing.features.payrollDesc': 'Intelligent payroll automation with smart statutory compliance. Auto-calculates EPF, SOCSO, EIS & PCB with machine learning accuracy.',
        'landing.features.leave': 'Smart Leave Management',
        'landing.features.leaveDesc': 'Predictive leave analytics and automated approval workflows. AI suggests optimal leave schedules for team productivity.',
        'landing.features.attendance': 'Intelligent Attendance',
        'landing.features.attendanceDesc': 'Real-time attendance tracking with anomaly detection. Geo-fencing and biometric-ready for future integrations.',
        'landing.features.performance': 'Performance Analytics',
        'landing.features.performanceDesc': 'Data-driven performance reviews with AI-generated insights. Track KPIs, goals, and employee growth trajectories.',
        'landing.features.reports': 'Predictive Reports',
        'landing.features.reportsDesc': 'Advanced HR analytics with forecasting dashboards. Visualize trends, predict attrition, and optimize workforce planning.',
        'landing.features.complianceEngine': 'Compliance Engine',
        'landing.features.complianceEngineDesc': '100% Malaysian statutory compliance. Auto-generates EA Forms, LHDN submissions, and all government-required documents.',
        
        // Landing Page - CTA
        'landing.cta.title': 'Start Your Free Trial Today',
        'landing.cta.subtitle': 'Join 500+ Malaysian companies already using MyHR Orbit.',
        'landing.cta.note': 'No credit card required. Set up in 5 minutes.',
        'landing.cta.scheduleDemo': 'Schedule a Demo',
        'landing.cta.startTrial': 'Start Free Trial',
        'landing.cta.badge': 'Ready to Transform Your HR?',
        
        // Landing Page - AI Showcase
        'landing.aiShowcase.badge': '✨ NEW: AI-Powered Features',
        'landing.aiShowcase.title': 'Meet Your AI HR Assistant',
        'landing.aiShowcase.subtitle': 'Experience the future of HR with our cutting-edge AI features. Automate tedious tasks, get instant insights, and make smarter decisions.',
        'landing.aiShowcase.chatbot': 'AI HR Chatbot',
        'landing.aiShowcase.chatbotDesc': 'Get instant answers to HR questions 24/7. Our AI assistant knows Malaysian employment laws, EPF, SOCSO, and company policies.',
        'landing.aiShowcase.scanner': 'Smart Receipt Scanner',
        'landing.aiShowcase.scannerDesc': 'Snap a photo of any receipt and let AI extract all details automatically. No more manual data entry for expense claims.',
        'landing.aiShowcase.insights': 'Performance Insights',
        'landing.aiShowcase.insightsDesc': 'Generate comprehensive performance analysis with one click. AI identifies strengths, improvement areas, and recommends goals.',
        'landing.aiShowcase.tryFree': 'Try AI Features Free',
        'landing.aiShowcase.liveNow': 'LIVE NOW',
        'landing.aiShowcase.ocrPowered': 'OCR POWERED',
        'landing.aiShowcase.aiAnalytics': 'AI ANALYTICS',
        
        // Landing Page - Modules
        'landing.modules.badge': 'Complete HR Suite',
        'landing.modules.title': 'Everything You Need, All in One Place',
        'landing.modules.subtitle': 'From onboarding to offboarding, MyHR Orbit covers every aspect of human resource management with intelligent automation.',
        'landing.modules.employees': 'Employee Management',
        'landing.modules.employeesFeatures': 'Complete employee profiles & documents|Onboarding & offboarding automation|Organization hierarchy visualization|Custom fields & data management',
        'landing.modules.payroll': 'Payroll Processing',
        'landing.modules.payrollFeatures': 'Automated salary calculations|EPF, SOCSO, EIS, PCB compliance|Bank file generation (all Malaysian banks)|Payslip generation & distribution',
        'landing.modules.leave': 'Leave & Attendance',
        'landing.modules.leaveFeatures': 'Multiple leave type configurations|Auto leave balance calculations|Time tracking & overtime management|Mobile check-in/check-out',
        'landing.modules.claims': 'Claims & Expenses',
        'landing.modules.claimsFeatures': 'Digital receipt uploads|Multi-level approval workflows|Expense policy enforcement|Integration with payroll',
        'landing.modules.performance': 'Performance Reviews',
        'landing.modules.performanceFeatures': 'Goal setting & tracking|360-degree feedback|Competency assessments|AI-powered performance insights',
        'landing.modules.reports': 'Reports & Compliance',
        'landing.modules.reportsFeatures': '50+ pre-built report templates|Custom report builder|EA Form & statutory reports|Export to Excel, PDF, CSV',
        
        // Landing Page - Compliance
        'landing.compliance.badge': 'Malaysian Statutory Compliance',
        'landing.compliance.title': 'Built for Malaysian Businesses',
        'landing.compliance.subtitle': '100% compliant with all Malaysian employment laws and statutory requirements. Stay worry-free with automatic updates.',
        'landing.compliance.epf': 'EPF/KWSP',
        'landing.compliance.epfDesc': 'Automatic calculations for all contribution categories',
        'landing.compliance.socso': 'SOCSO/PERKESO',
        'landing.compliance.socsoDesc': 'Employment Injury & Invalidity Scheme calculations',
        'landing.compliance.eis': 'EIS/SIP',
        'landing.compliance.eisDesc': 'Employment Insurance System deductions',
        'landing.compliance.pcb': 'PCB/MTD',
        'landing.compliance.pcbDesc': 'Monthly Tax Deduction using latest LHDN rates',
        
        // Landing Page - Forms
        'landing.forms.badge': 'One-Click Downloads',
        'landing.forms.title': 'Malaysian Government Forms',
        'landing.forms.subtitle': 'Generate compliant statutory forms with just one click. All forms are automatically populated with your company and employee data.',
        
        // Landing Page - Comparison
        'landing.comparison.badge': 'Why Choose Us',
        'landing.comparison.title': 'Why MyHR Orbit is the Smarter Choice',
        'landing.comparison.subtitle': 'See how we compare to other HRMS solutions in the Malaysian market.',
        'landing.comparison.feature': 'Feature',
        'landing.comparison.mobileCheckin': 'Mobile Check-in/out',
        'landing.comparison.aiScan': 'Claims with AI Scan',
        'landing.comparison.bahasaUI': 'Bahasa Malaysia UI',
        'landing.comparison.setupTime': 'Setup Time',
        'landing.comparison.pricing': 'Pricing',
        'landing.comparison.mins': '5 mins',
        'landing.comparison.hours': 'Hours/Days',
        'landing.comparison.free': 'RM 0',
        'landing.comparison.paid': 'RM 8-15+/user',
        
        // Landing Page - Testimonials
        'landing.testimonials.badge': 'Success Stories',
        'landing.testimonials.title': 'Trusted by Malaysian Businesses',
        
        // Landing Page - Footer
        'landing.footer.description': 'Malaysia\'s most intelligent HRMS platform. Automate payroll, streamline HR operations, and ensure 100% statutory compliance with AI-powered solutions.',
        'landing.footer.product': 'Product',
        'landing.footer.features': 'Features',
        'landing.footer.pricing': 'Pricing',
        'landing.footer.integrations': 'Integrations',
        'landing.footer.api': 'API Documentation',
        'landing.footer.company': 'Company',
        'landing.footer.about': 'About Us',
        'landing.footer.careers': 'Careers',
        'landing.footer.privacy': 'Privacy Policy',
        'landing.footer.terms': 'Terms of Service',
        'landing.footer.copyright': '© 2026 MyHR Orbit. All rights reserved.',
        'landing.footer.madeIn': 'Made with ❤️ in Malaysia',
        
        // Login Page
        'login.title': 'Complete HR Management Solution',
        'login.subtitle': 'Streamline payroll, leave, attendance, and performance management with full Malaysia statutory compliance.',
        'login.compliance': 'EPF, SOCSO, EIS & PCB Compliance',
        'login.welcome': 'Welcome Back',
        'login.signInTo': 'Sign in to your account',
        'login.email': 'Email',
        'login.password': 'Password',
        'login.signIn': 'Sign In',
        'login.noAccount': 'Don\'t have an account?',
        'login.createAccount': 'Create Company Account',
        'login.platformOwner': 'Platform Owner Access',
        'login.platformOwnerDesc': 'Access all companies, manage platform, view analytics',
        'login.demoCredentials': 'Demo Credentials',
        'login.hrManager': 'HR Manager',
        'login.employee': 'Employee',
        'login.anyImported': 'Any imported employee',

        // Navigation
        'nav.dashboard': 'Dashboard',
        'nav.employees': 'Employees',
        'nav.myProfile': 'My Profile',
        'nav.leave': 'Leave',
        'nav.attendance': 'Attendance',
        'nav.claims': 'Claims',
        'nav.payroll': 'Payroll',
        'nav.payslips': 'Payslips',
        'nav.overtime': 'Overtime',
        'nav.probation': 'Probation',
        'nav.performance': 'Performance',
        'nav.documents': 'Documents',
        'nav.reports': 'Reports',
        'nav.forms': 'Forms',
        'nav.hrInbox': 'HR Inbox',
        'nav.settings': 'Settings',
        'nav.logout': 'Logout',
        'nav.import': 'Import Data',
        'nav.migration': 'Data Migration',
        'nav.aiConfig': 'AI Config',
        'nav.licenseSupport': 'License & Support',
        'nav.psclAdmin': 'PSCL Admin',
        'nav.platformAdmin': 'Platform Admin',
        'nav.myTeam': 'My Team',
        
        // Common
        'common.search': 'Search',
        'common.filter': 'Filter',
        'common.save': 'Save',
        'common.cancel': 'Cancel',
        'common.edit': 'Edit',
        'common.delete': 'Delete',
        'common.download': 'Download',
        'common.submit': 'Submit',
        'common.approve': 'Approve',
        'common.reject': 'Reject',
        'common.pending': 'Pending',
        'common.approved': 'Approved',
        'common.rejected': 'Rejected',
        'common.loading': 'Loading...',
        'common.actions': 'Actions',
        'common.status': 'Status',
        'common.date': 'Date',
        'common.name': 'Name',
        'common.email': 'Email',
        'common.phone': 'Phone',
        'common.department': 'Department',
        'common.position': 'Position',
        'common.all': 'All',
        'common.active': 'Active',
        'common.inactive': 'Inactive',
        'common.view': 'View',
        'common.add': 'Add',
        'common.update': 'Update',
        'common.close': 'Close',
        'common.yes': 'Yes',
        'common.no': 'No',
        'common.confirm': 'Confirm',
        'common.back': 'Back',
        'common.next': 'Next',
        'common.previous': 'Previous',
        'common.noData': 'No data available',
        'common.clickToView': 'Click to view details',
        
        // Dashboard
        'dashboard.title': 'Dashboard',
        'dashboard.welcome': 'Welcome back',
        'dashboard.subtitle': "Here's what's happening with your organization today.",
        'dashboard.totalEmployees': 'Total Employees',
        'dashboard.pendingLeaves': 'Pending Leaves',
        'dashboard.pendingClaims': 'Pending Claims',
        'dashboard.monthlyPayroll': 'Monthly Payroll',
        'dashboard.employeeGrowth': 'Employee Growth',
        'dashboard.departmentDistribution': 'Department Distribution',
        'dashboard.recentActivity': 'Recent Activity',
        'dashboard.quickActions': 'Quick Actions',
        'dashboard.upcomingLeaves': 'Upcoming Leaves',
        'dashboard.pendingApprovals': 'Pending Approvals',
        'dashboard.addEmployee': 'Add Employee',
        'dashboard.processPayroll': 'Process Payroll',
        'dashboard.reviewLeaves': 'Review Leaves',
        'dashboard.viewReports': 'View Reports',
        
        // AI Features
        'ai.chatbot': 'AI HR Assistant',
        'ai.askQuestion': 'Ask a question...',
        'ai.thinking': 'Thinking...',
        'ai.quickQuestions': 'Quick Questions',
        'ai.receiptScanner': 'AI Receipt Scanner',
        'ai.scanningReceipt': 'Scanning receipt...',
        'ai.performanceInsights': 'AI Performance Insights',
        'ai.generatingInsights': 'Generating insights...',
        
        // Auth
        'auth.login': 'Login',
        'auth.logout': 'Logout',
        'auth.email': 'Email',
        'auth.password': 'Password',
        'auth.forgotPassword': 'Forgot Password?',
        'auth.signIn': 'Sign In',
        'auth.signUp': 'Sign Up',
        'auth.rememberMe': 'Remember Me',
      },
      ms: {
        // Landing Page - Header
        'landing.nav.features': 'Ciri-ciri',
        'landing.nav.pricing': 'Harga',
        'landing.nav.contact': 'Hubungi Jualan',
        'landing.nav.signIn': 'Log Masuk',
        'landing.nav.getStarted': 'Mula Percuma',
        
        // Landing Page - Hero
        'landing.hero.badge': 'HRMS Berkuasa AI untuk Perniagaan Malaysia',
        'landing.hero.title1': 'Masa Depan HR adalah',
        'landing.hero.title2': 'Pintar & Automatik',
        'landing.hero.subtitle': 'Transformasikan operasi HR anda dengan HRMS berkuasa AI yang paling canggih di Malaysia. Automatikkan gaji, permudahkan pengurusan cuti, pastikan 100% pematuhan statutori, dan buka analitik tenaga kerja yang berkuasa — semuanya dalam satu platform pintar.',
        'landing.hero.bookDemo': 'Tempah Demo Percuma',
        'landing.hero.startTrial': 'Mula Percubaan 14 Hari',
        'landing.hero.stat1': 'Pekerja Diuruskan',
        'landing.hero.stat2': 'Syarikat Percaya Kami',
        'landing.hero.stat3': 'Jaminan Uptime',
        'landing.hero.stat4': 'Dijimatkan Sebulan',
        
        // Landing Page - Benefits
        'landing.benefits.compliance': '100% Pematuhan Malaysia',
        'landing.benefits.cloud': 'Akses Berasaskan Awan',
        'landing.benefits.security': 'Keselamatan Perusahaan',
        'landing.benefits.ai': 'Automasi Berkuasa AI',
        'landing.benefits.setup': 'Persediaan 5 Minit',
        'landing.benefits.support': 'Sokongan Premium 24/7',
        
        // Landing Page - Features
        'landing.features.badge': 'Ciri-ciri Berkuasa AI',
        'landing.features.title': 'Automasi HR Pintar',
        'landing.features.subtitle': 'Manfaatkan AI dan pembelajaran mesin terkini untuk mengautomasikan proses HR yang kompleks, mengurangkan ralat manual, dan membuat keputusan tenaga kerja berdasarkan data.',
        'landing.features.payroll': 'Gaji Berkuasa AI',
        'landing.features.payrollDesc': 'Automasi gaji pintar dengan pematuhan statutori. Auto-kira EPF, SOCSO, EIS & PCB dengan ketepatan pembelajaran mesin.',
        'landing.features.leave': 'Pengurusan Cuti Pintar',
        'landing.features.leaveDesc': 'Analitik cuti ramalan dan aliran kerja kelulusan automatik. AI mencadangkan jadual cuti optimum untuk produktiviti pasukan.',
        'landing.features.attendance': 'Kehadiran Pintar',
        'landing.features.attendanceDesc': 'Pengesanan kehadiran masa nyata dengan pengesanan anomali. Sedia geo-fencing dan biometrik untuk integrasi masa depan.',
        'landing.features.performance': 'Analitik Prestasi',
        'landing.features.performanceDesc': 'Ulasan prestasi berdasarkan data dengan pandangan AI. Jejaki KPI, matlamat, dan trajektori pertumbuhan pekerja.',
        'landing.features.reports': 'Laporan Ramalan',
        'landing.features.reportsDesc': 'Analitik HR lanjutan dengan papan pemuka ramalan. Visualisasikan trend, ramalkan pengurangan, dan optimumkan perancangan tenaga kerja.',
        'landing.features.complianceEngine': 'Enjin Pematuhan',
        'landing.features.complianceEngineDesc': '100% pematuhan statutori Malaysia. Auto-jana Borang EA, penghantaran LHDN, dan semua dokumen yang diperlukan kerajaan.',
        
        // Landing Page - CTA
        'landing.cta.title': 'Mula Percubaan Percuma Hari Ini',
        'landing.cta.subtitle': 'Sertai 500+ syarikat Malaysia yang sudah menggunakan MyHR Orbit.',
        'landing.cta.note': 'Tiada kad kredit diperlukan. Sediakan dalam 5 minit.',
        'landing.cta.scheduleDemo': 'Jadualkan Demo',
        'landing.cta.startTrial': 'Mula Percubaan',
        'landing.cta.badge': 'Bersedia Transformasi HR Anda?',
        
        // Landing Page - AI Showcase
        'landing.aiShowcase.badge': '✨ BARU: Ciri Berkuasa AI',
        'landing.aiShowcase.title': 'Temui Pembantu HR AI Anda',
        'landing.aiShowcase.subtitle': 'Alami masa depan HR dengan ciri AI terkini kami. Automatikkan tugas membosankan, dapatkan pandangan segera, dan buat keputusan lebih bijak.',
        'landing.aiShowcase.chatbot': 'Chatbot HR AI',
        'landing.aiShowcase.chatbotDesc': 'Dapatkan jawapan segera untuk soalan HR 24/7. Pembantu AI kami tahu undang-undang pekerjaan Malaysia, EPF, SOCSO, dan polisi syarikat.',
        'landing.aiShowcase.scanner': 'Pengimbas Resit Pintar',
        'landing.aiShowcase.scannerDesc': 'Ambil gambar resit dan biarkan AI mengekstrak semua butiran secara automatik. Tiada lagi kemasukan data manual untuk tuntutan perbelanjaan.',
        'landing.aiShowcase.insights': 'Pandangan Prestasi',
        'landing.aiShowcase.insightsDesc': 'Jana analisis prestasi komprehensif dengan satu klik. AI mengenal pasti kekuatan, bidang penambahbaikan, dan mengesyorkan matlamat.',
        'landing.aiShowcase.tryFree': 'Cuba Ciri AI Percuma',
        'landing.aiShowcase.liveNow': 'LANGSUNG',
        'landing.aiShowcase.ocrPowered': 'BERKUASA OCR',
        'landing.aiShowcase.aiAnalytics': 'ANALITIK AI',
        
        // Landing Page - Modules
        'landing.modules.badge': 'Suite HR Lengkap',
        'landing.modules.title': 'Semua yang Anda Perlukan, Dalam Satu Tempat',
        'landing.modules.subtitle': 'Dari onboarding ke offboarding, MyHR Orbit meliputi setiap aspek pengurusan sumber manusia dengan automasi pintar.',
        'landing.modules.employees': 'Pengurusan Pekerja',
        'landing.modules.employeesFeatures': 'Profil & dokumen pekerja lengkap|Automasi onboarding & offboarding|Visualisasi hierarki organisasi|Medan tersuai & pengurusan data',
        'landing.modules.payroll': 'Pemprosesan Gaji',
        'landing.modules.payrollFeatures': 'Pengiraan gaji automatik|Pematuhan EPF, SOCSO, EIS, PCB|Penjanaan fail bank (semua bank Malaysia)|Penjanaan & pengedaran slip gaji',
        'landing.modules.leave': 'Cuti & Kehadiran',
        'landing.modules.leaveFeatures': 'Konfigurasi pelbagai jenis cuti|Pengiraan baki cuti automatik|Pengurusan masa & kerja lebih masa|Daftar masuk/keluar mudah alih',
        'landing.modules.claims': 'Tuntutan & Perbelanjaan',
        'landing.modules.claimsFeatures': 'Muat naik resit digital|Aliran kerja kelulusan berbilang peringkat|Penguatkuasaan polisi perbelanjaan|Integrasi dengan gaji',
        'landing.modules.performance': 'Ulasan Prestasi',
        'landing.modules.performanceFeatures': 'Penetapan & penjejakan matlamat|Maklum balas 360 darjah|Penilaian kecekapan|Pandangan prestasi berkuasa AI',
        'landing.modules.reports': 'Laporan & Pematuhan',
        'landing.modules.reportsFeatures': '50+ templat laporan sedia ada|Pembina laporan tersuai|Borang EA & laporan statutori|Eksport ke Excel, PDF, CSV',
        
        // Landing Page - Compliance
        'landing.compliance.badge': 'Pematuhan Statutori Malaysia',
        'landing.compliance.title': 'Dibina untuk Perniagaan Malaysia',
        'landing.compliance.subtitle': '100% patuh dengan semua undang-undang pekerjaan Malaysia dan keperluan statutori. Kekal tenang dengan kemas kini automatik.',
        'landing.compliance.epf': 'EPF/KWSP',
        'landing.compliance.epfDesc': 'Pengiraan automatik untuk semua kategori caruman',
        'landing.compliance.socso': 'SOCSO/PERKESO',
        'landing.compliance.socsoDesc': 'Pengiraan Skim Kecederaan Pekerjaan & Hilang Upaya',
        'landing.compliance.eis': 'EIS/SIP',
        'landing.compliance.eisDesc': 'Potongan Sistem Insurans Pekerjaan',
        'landing.compliance.pcb': 'PCB/MTD',
        'landing.compliance.pcbDesc': 'Potongan Cukai Bulanan menggunakan kadar LHDN terkini',
        
        // Landing Page - Forms
        'landing.forms.badge': 'Muat Turun Satu Klik',
        'landing.forms.title': 'Borang Kerajaan Malaysia',
        'landing.forms.subtitle': 'Jana borang statutori yang patuh dengan hanya satu klik. Semua borang diisi secara automatik dengan data syarikat dan pekerja anda.',
        
        // Landing Page - Comparison
        'landing.comparison.badge': 'Kenapa Pilih Kami',
        'landing.comparison.title': 'Kenapa MyHR Orbit Pilihan Lebih Bijak',
        'landing.comparison.subtitle': 'Lihat bagaimana kami berbanding dengan penyelesaian HRMS lain di pasaran Malaysia.',
        'landing.comparison.feature': 'Ciri',
        'landing.comparison.mobileCheckin': 'Daftar Masuk/Keluar Mudah Alih',
        'landing.comparison.aiScan': 'Tuntutan dengan Imbasan AI',
        'landing.comparison.bahasaUI': 'Antaramuka Bahasa Malaysia',
        'landing.comparison.setupTime': 'Masa Persediaan',
        'landing.comparison.pricing': 'Harga',
        'landing.comparison.mins': '5 minit',
        'landing.comparison.hours': 'Jam/Hari',
        'landing.comparison.free': 'RM 0',
        'landing.comparison.paid': 'RM 8-15+/pengguna',
        
        // Landing Page - Testimonials
        'landing.testimonials.badge': 'Kisah Kejayaan',
        'landing.testimonials.title': 'Dipercayai oleh Perniagaan Malaysia',
        
        // Landing Page - Footer
        'landing.footer.description': 'Platform HRMS paling pintar di Malaysia. Automatikkan gaji, permudahkan operasi HR, dan pastikan 100% pematuhan statutori dengan penyelesaian berkuasa AI.',
        'landing.footer.product': 'Produk',
        'landing.footer.features': 'Ciri-ciri',
        'landing.footer.pricing': 'Harga',
        'landing.footer.integrations': 'Integrasi',
        'landing.footer.api': 'Dokumentasi API',
        'landing.footer.company': 'Syarikat',
        'landing.footer.about': 'Tentang Kami',
        'landing.footer.careers': 'Kerjaya',
        'landing.footer.privacy': 'Dasar Privasi',
        'landing.footer.terms': 'Terma Perkhidmatan',
        'landing.footer.copyright': '© 2026 MyHR Orbit. Hak cipta terpelihara.',
        'landing.footer.madeIn': 'Dibuat dengan ❤️ di Malaysia',
        
        // Login Page
        'login.title': 'Penyelesaian Pengurusan HR Lengkap',
        'login.subtitle': 'Permudahkan gaji, cuti, kehadiran, dan pengurusan prestasi dengan pematuhan statutori Malaysia sepenuhnya.',
        'login.compliance': 'Pematuhan EPF, SOCSO, EIS & PCB',
        'login.welcome': 'Selamat Kembali',
        'login.signInTo': 'Log masuk ke akaun anda',
        'login.email': 'Emel',
        'login.password': 'Kata Laluan',
        'login.signIn': 'Log Masuk',
        'login.noAccount': 'Tiada akaun?',
        'login.createAccount': 'Cipta Akaun Syarikat',
        'login.platformOwner': 'Akses Pemilik Platform',
        'login.platformOwnerDesc': 'Akses semua syarikat, urus platform, lihat analitik',
        'login.demoCredentials': 'Kredensial Demo',
        'login.hrManager': 'Pengurus HR',
        'login.employee': 'Pekerja',
        'login.anyImported': 'Mana-mana pekerja diimport',

        // Navigation
        'nav.dashboard': 'Papan Pemuka',
        'nav.employees': 'Pekerja',
        'nav.myProfile': 'Profil Saya',
        'nav.leave': 'Cuti',
        'nav.attendance': 'Kehadiran',
        'nav.claims': 'Tuntutan',
        'nav.payroll': 'Gaji',
        'nav.payslips': 'Slip Gaji',
        'nav.overtime': 'Kerja Lebih Masa',
        'nav.probation': 'Tempoh Percubaan',
        'nav.performance': 'Prestasi',
        'nav.documents': 'Dokumen',
        'nav.reports': 'Laporan',
        'nav.forms': 'Borang',
        'nav.hrInbox': 'Peti Masuk HR',
        'nav.settings': 'Tetapan',
        'nav.logout': 'Log Keluar',
        'nav.import': 'Import Data',
        'nav.migration': 'Migrasi Data',
        'nav.aiConfig': 'Konfigurasi AI',
        'nav.licenseSupport': 'Lesen & Sokongan',
        'nav.psclAdmin': 'Admin PSCL',
        'nav.platformAdmin': 'Admin Platform',
        'nav.myTeam': 'Pasukan Saya',
        
        // Common
        'common.search': 'Cari',
        'common.filter': 'Tapis',
        'common.save': 'Simpan',
        'common.cancel': 'Batal',
        'common.edit': 'Edit',
        'common.delete': 'Padam',
        'common.download': 'Muat Turun',
        'common.submit': 'Hantar',
        'common.approve': 'Luluskan',
        'common.reject': 'Tolak',
        'common.pending': 'Menunggu',
        'common.approved': 'Diluluskan',
        'common.rejected': 'Ditolak',
        'common.loading': 'Memuatkan...',
        'common.actions': 'Tindakan',
        'common.status': 'Status',
        'common.date': 'Tarikh',
        'common.name': 'Nama',
        'common.email': 'Emel',
        'common.phone': 'Telefon',
        'common.department': 'Jabatan',
        'common.position': 'Jawatan',
        'common.all': 'Semua',
        'common.active': 'Aktif',
        'common.inactive': 'Tidak Aktif',
        'common.view': 'Lihat',
        'common.add': 'Tambah',
        'common.update': 'Kemaskini',
        'common.close': 'Tutup',
        'common.yes': 'Ya',
        'common.no': 'Tidak',
        'common.confirm': 'Sahkan',
        'common.back': 'Kembali',
        'common.next': 'Seterusnya',
        'common.previous': 'Sebelumnya',
        'common.noData': 'Tiada data tersedia',
        'common.clickToView': 'Klik untuk lihat butiran',
        
        // Dashboard
        'dashboard.title': 'Papan Pemuka',
        'dashboard.welcome': 'Selamat kembali',
        'dashboard.subtitle': 'Berikut adalah perkembangan organisasi anda hari ini.',
        'dashboard.totalEmployees': 'Jumlah Pekerja',
        'dashboard.pendingLeaves': 'Cuti Menunggu',
        'dashboard.pendingClaims': 'Tuntutan Menunggu',
        'dashboard.monthlyPayroll': 'Gaji Bulanan',
        'dashboard.employeeGrowth': 'Pertumbuhan Pekerja',
        'dashboard.departmentDistribution': 'Taburan Jabatan',
        'dashboard.recentActivity': 'Aktiviti Terkini',
        'dashboard.quickActions': 'Tindakan Pantas',
        'dashboard.upcomingLeaves': 'Cuti Akan Datang',
        'dashboard.pendingApprovals': 'Kelulusan Menunggu',
        'dashboard.addEmployee': 'Tambah Pekerja',
        'dashboard.processPayroll': 'Proses Gaji',
        'dashboard.reviewLeaves': 'Semak Cuti',
        'dashboard.viewReports': 'Lihat Laporan',
        
        // AI Features
        'ai.chatbot': 'Pembantu HR AI',
        'ai.askQuestion': 'Tanya soalan...',
        'ai.thinking': 'Sedang berfikir...',
        'ai.quickQuestions': 'Soalan Pantas',
        'ai.receiptScanner': 'Pengimbas Resit AI',
        'ai.scanningReceipt': 'Mengimbas resit...',
        'ai.performanceInsights': 'Pandangan Prestasi AI',
        'ai.generatingInsights': 'Menjana pandangan...',
        
        // Auth
        'auth.login': 'Log Masuk',
        'auth.logout': 'Log Keluar',
        'auth.email': 'Emel',
        'auth.password': 'Kata Laluan',
        'auth.forgotPassword': 'Lupa Kata Laluan?',
        'auth.signIn': 'Log Masuk',
        'auth.signUp': 'Daftar',
        'auth.rememberMe': 'Ingat Saya',
      },
      zh: {
        // Landing Page - Header
        'landing.nav.features': '功能',
        'landing.nav.pricing': '价格',
        'landing.nav.contact': '联系销售',
        'landing.nav.signIn': '登录',
        'landing.nav.getStarted': '免费开始',
        
        // Landing Page - Hero
        'landing.hero.badge': '马来西亚企业的AI人力资源管理系统',
        'landing.hero.title1': '人力资源的未来是',
        'landing.hero.title2': '智能与自动化',
        'landing.hero.subtitle': '使用马来西亚最先进的AI人力资源管理系统转变您的HR运营。自动化薪资、简化休假管理、确保100%法定合规，并解锁强大的劳动力分析——一切尽在一个智能平台中。',
        'landing.hero.bookDemo': '预约免费演示',
        'landing.hero.startTrial': '开始14天免费试用',
        'landing.hero.stat1': '管理员工',
        'landing.hero.stat2': '信任我们的公司',
        'landing.hero.stat3': '正常运行保证',
        'landing.hero.stat4': '每月节省',
        
        // Landing Page - Benefits
        'landing.benefits.compliance': '100%马来西亚合规',
        'landing.benefits.cloud': '云端随时访问',
        'landing.benefits.security': '企业级安全',
        'landing.benefits.ai': 'AI驱动自动化',
        'landing.benefits.setup': '5分钟设置',
        'landing.benefits.support': '24/7高级支持',
        
        // Landing Page - Features
        'landing.features.badge': 'AI驱动功能',
        'landing.features.title': '智能HR自动化',
        'landing.features.subtitle': '利用最先进的AI和机器学习来自动化复杂的HR流程，减少人工错误，并做出基于数据的劳动力决策。',
        'landing.features.payroll': 'AI薪资管理',
        'landing.features.payrollDesc': '智能薪资自动化与法定合规。通过机器学习精度自动计算EPF、SOCSO、EIS和PCB。',
        'landing.features.leave': '智能休假管理',
        'landing.features.leaveDesc': '预测性休假分析和自动审批工作流程。AI建议团队生产力的最佳休假时间表。',
        'landing.features.attendance': '智能考勤',
        'landing.features.attendanceDesc': '实时考勤跟踪与异常检测。为未来集成准备地理围栏和生物识别。',
        'landing.features.performance': '绩效分析',
        'landing.features.performanceDesc': '数据驱动的绩效评估与AI生成的洞察。跟踪KPI、目标和员工成长轨迹。',
        'landing.features.reports': '预测报告',
        'landing.features.reportsDesc': '高级HR分析与预测仪表板。可视化趋势、预测流失并优化劳动力规划。',
        'landing.features.complianceEngine': '合规引擎',
        'landing.features.complianceEngineDesc': '100%马来西亚法定合规。自动生成EA表格、LHDN提交和所有政府要求的文件。',
        
        // Landing Page - CTA
        'landing.cta.title': '今天开始免费试用',
        'landing.cta.subtitle': '加入500多家已使用MyHR Orbit的马来西亚公司。',
        'landing.cta.note': '无需信用卡。5分钟内设置完成。',
        'landing.cta.scheduleDemo': '安排演示',
        'landing.cta.startTrial': '开始免费试用',
        'landing.cta.badge': '准备好转变您的HR了吗？',
        
        // Landing Page - AI Showcase
        'landing.aiShowcase.badge': '✨ 全新：AI驱动功能',
        'landing.aiShowcase.title': '认识您的AI HR助手',
        'landing.aiShowcase.subtitle': '体验HR的未来，使用我们尖端的AI功能。自动化繁琐任务，获取即时洞察，做出更明智的决策。',
        'landing.aiShowcase.chatbot': 'AI HR聊天机器人',
        'landing.aiShowcase.chatbotDesc': '全天候即时获取HR问题的答案。我们的AI助手了解马来西亚劳动法、EPF、SOCSO和公司政策。',
        'landing.aiShowcase.scanner': '智能收据扫描',
        'landing.aiShowcase.scannerDesc': '拍摄任何收据的照片，让AI自动提取所有详细信息。不再需要为费用报销手动输入数据。',
        'landing.aiShowcase.insights': '绩效洞察',
        'landing.aiShowcase.insightsDesc': '一键生成全面的绩效分析。AI识别优势、改进领域并推荐目标。',
        'landing.aiShowcase.tryFree': '免费试用AI功能',
        'landing.aiShowcase.liveNow': '已上线',
        'landing.aiShowcase.ocrPowered': 'OCR驱动',
        'landing.aiShowcase.aiAnalytics': 'AI分析',
        
        // Landing Page - Modules
        'landing.modules.badge': '完整HR套件',
        'landing.modules.title': '您所需的一切，尽在一处',
        'landing.modules.subtitle': '从入职到离职，MyHR Orbit通过智能自动化涵盖人力资源管理的各个方面。',
        'landing.modules.employees': '员工管理',
        'landing.modules.employeesFeatures': '完整的员工档案和文件|入职和离职自动化|组织层级可视化|自定义字段和数据管理',
        'landing.modules.payroll': '薪资处理',
        'landing.modules.payrollFeatures': '自动工资计算|EPF、SOCSO、EIS、PCB合规|银行文件生成（所有马来西亚银行）|工资单生成和分发',
        'landing.modules.leave': '休假与考勤',
        'landing.modules.leaveFeatures': '多种休假类型配置|自动休假余额计算|时间跟踪和加班管理|移动签到/签退',
        'landing.modules.claims': '报销与费用',
        'landing.modules.claimsFeatures': '数字收据上传|多级审批流程|费用政策执行|与薪资集成',
        'landing.modules.performance': '绩效评估',
        'landing.modules.performanceFeatures': '目标设定与跟踪|360度反馈|能力评估|AI驱动的绩效洞察',
        'landing.modules.reports': '报告与合规',
        'landing.modules.reportsFeatures': '50+预建报告模板|自定义报告生成器|EA表格和法定报告|导出到Excel、PDF、CSV',
        
        // Landing Page - Compliance
        'landing.compliance.badge': '马来西亚法定合规',
        'landing.compliance.title': '为马来西亚企业打造',
        'landing.compliance.subtitle': '100%符合所有马来西亚就业法和法定要求。通过自动更新保持无忧。',
        'landing.compliance.epf': 'EPF/KWSP',
        'landing.compliance.epfDesc': '所有缴费类别的自动计算',
        'landing.compliance.socso': 'SOCSO/PERKESO',
        'landing.compliance.socsoDesc': '工伤和残疾计划计算',
        'landing.compliance.eis': 'EIS/SIP',
        'landing.compliance.eisDesc': '就业保险系统扣除',
        'landing.compliance.pcb': 'PCB/MTD',
        'landing.compliance.pcbDesc': '使用最新LHDN税率的月度税务扣除',
        
        // Landing Page - Forms
        'landing.forms.badge': '一键下载',
        'landing.forms.title': '马来西亚政府表格',
        'landing.forms.subtitle': '只需一键即可生成合规的法定表格。所有表格都会自动填充您的公司和员工数据。',
        
        // Landing Page - Comparison
        'landing.comparison.badge': '为什么选择我们',
        'landing.comparison.title': '为什么MyHR Orbit是更明智的选择',
        'landing.comparison.subtitle': '看看我们与马来西亚市场上其他HRMS解决方案的比较。',
        'landing.comparison.feature': '功能',
        'landing.comparison.mobileCheckin': '移动签到/签退',
        'landing.comparison.aiScan': 'AI扫描报销',
        'landing.comparison.bahasaUI': '马来语界面',
        'landing.comparison.setupTime': '设置时间',
        'landing.comparison.pricing': '定价',
        'landing.comparison.mins': '5分钟',
        'landing.comparison.hours': '数小时/天',
        'landing.comparison.free': 'RM 0',
        'landing.comparison.paid': 'RM 8-15+/用户',
        
        // Landing Page - Testimonials
        'landing.testimonials.badge': '成功故事',
        'landing.testimonials.title': '受马来西亚企业信赖',
        
        // Landing Page - Footer
        'landing.footer.description': '马来西亚最智能的人力资源管理平台。通过AI解决方案自动化薪资、简化HR运营并确保100%法定合规。',
        'landing.footer.product': '产品',
        'landing.footer.features': '功能',
        'landing.footer.pricing': '价格',
        'landing.footer.integrations': '集成',
        'landing.footer.api': 'API文档',
        'landing.footer.company': '公司',
        'landing.footer.about': '关于我们',
        'landing.footer.careers': '职业机会',
        'landing.footer.privacy': '隐私政策',
        'landing.footer.terms': '服务条款',
        'landing.footer.copyright': '© 2026 MyHR Orbit。保留所有权利。',
        'landing.footer.madeIn': '在马来西亚用❤️制作',
        
        // Login Page
        'login.title': '完整的人力资源管理解决方案',
        'login.subtitle': '简化薪资、休假、考勤和绩效管理，完全符合马来西亚法定要求。',
        'login.compliance': 'EPF、SOCSO、EIS和PCB合规',
        'login.welcome': '欢迎回来',
        'login.signInTo': '登录您的账户',
        'login.email': '电子邮件',
        'login.password': '密码',
        'login.signIn': '登录',
        'login.noAccount': '没有账户？',
        'login.createAccount': '创建公司账户',
        'login.platformOwner': '平台所有者访问',
        'login.platformOwnerDesc': '访问所有公司、管理平台、查看分析',
        'login.demoCredentials': '演示凭证',
        'login.hrManager': 'HR经理',
        'login.employee': '员工',
        'login.anyImported': '任何导入的员工',

        // Navigation
        'nav.dashboard': '仪表板',
        'nav.employees': '员工',
        'nav.myProfile': '我的资料',
        'nav.leave': '休假',
        'nav.attendance': '考勤',
        'nav.claims': '报销',
        'nav.payroll': '薪资',
        'nav.payslips': '工资单',
        'nav.overtime': '加班',
        'nav.probation': '试用期',
        'nav.performance': '绩效',
        'nav.documents': '文件',
        'nav.reports': '报告',
        'nav.forms': '表格',
        'nav.hrInbox': 'HR 收件箱',
        'nav.settings': '设置',
        'nav.logout': '退出登录',
        'nav.import': '导入数据',
        'nav.migration': '数据迁移',
        'nav.aiConfig': 'AI 配置',
        'nav.licenseSupport': '许可证与支持',
        'nav.psclAdmin': 'PSCL 管理',
        'nav.platformAdmin': '平台管理',
        'nav.myTeam': '我的团队',
        
        // Common
        'common.search': '搜索',
        'common.filter': '筛选',
        'common.save': '保存',
        'common.cancel': '取消',
        'common.edit': '编辑',
        'common.delete': '删除',
        'common.download': '下载',
        'common.submit': '提交',
        'common.approve': '批准',
        'common.reject': '拒绝',
        'common.pending': '待处理',
        'common.approved': '已批准',
        'common.rejected': '已拒绝',
        'common.loading': '加载中...',
        'common.actions': '操作',
        'common.status': '状态',
        'common.date': '日期',
        'common.name': '姓名',
        'common.email': '电子邮件',
        'common.phone': '电话',
        'common.department': '部门',
        'common.position': '职位',
        'common.all': '全部',
        'common.active': '活跃',
        'common.inactive': '非活跃',
        'common.view': '查看',
        'common.add': '添加',
        'common.update': '更新',
        'common.close': '关闭',
        'common.yes': '是',
        'common.no': '否',
        'common.confirm': '确认',
        'common.back': '返回',
        'common.next': '下一步',
        'common.previous': '上一步',
        'common.noData': '暂无数据',
        'common.clickToView': '点击查看详情',
        
        // Dashboard
        'dashboard.title': '仪表板',
        'dashboard.welcome': '欢迎回来',
        'dashboard.subtitle': '以下是您组织今天的动态。',
        'dashboard.totalEmployees': '员工总数',
        'dashboard.pendingLeaves': '待批休假',
        'dashboard.pendingClaims': '待批报销',
        'dashboard.monthlyPayroll': '月薪资',
        'dashboard.employeeGrowth': '员工增长',
        'dashboard.departmentDistribution': '部门分布',
        'dashboard.recentActivity': '最近活动',
        'dashboard.quickActions': '快速操作',
        'dashboard.upcomingLeaves': '即将休假',
        'dashboard.pendingApprovals': '待批审批',
        'dashboard.addEmployee': '添加员工',
        'dashboard.processPayroll': '处理薪资',
        'dashboard.reviewLeaves': '审核休假',
        'dashboard.viewReports': '查看报告',
        
        // AI Features
        'ai.chatbot': 'AI HR助手',
        'ai.askQuestion': '提问...',
        'ai.thinking': '思考中...',
        'ai.quickQuestions': '快速问题',
        'ai.receiptScanner': 'AI收据扫描',
        'ai.scanningReceipt': '扫描收据中...',
        'ai.performanceInsights': 'AI绩效洞察',
        'ai.generatingInsights': '生成洞察中...',
        
        // Auth
        'auth.login': '登录',
        'auth.logout': '退出',
        'auth.email': '电子邮件',
        'auth.password': '密码',
        'auth.forgotPassword': '忘记密码？',
        'auth.signIn': '登录',
        'auth.signUp': '注册',
        'auth.rememberMe': '记住我',
      },
      ta: {
        // Landing Page - Header
        'landing.nav.features': 'அம்சங்கள்',
        'landing.nav.pricing': 'விலை',
        'landing.nav.contact': 'விற்பனையை தொடர்பு கொள்ள',
        'landing.nav.signIn': 'உள்நுழை',
        'landing.nav.getStarted': 'இலவசமாக தொடங்கு',
        
        // Landing Page - Hero
        'landing.hero.badge': 'மலேசிய வணிகங்களுக்கான AI HRMS',
        'landing.hero.title1': 'HR-ன் எதிர்காலம்',
        'landing.hero.title2': 'புத்திசாலி & தானியங்கி',
        'landing.hero.subtitle': 'மலேசியாவின் மிகவும் மேம்பட்ட AI-உந்துதல் HRMS மூலம் உங்கள் HR செயல்பாடுகளை மாற்றுங்கள். சம்பளத்தை தானியங்கு செய்யுங்கள், விடுப்பு மேலாண்மையை எளிதாக்குங்கள், 100% சட்டப்பூர்வ இணக்கத்தை உறுதி செய்யுங்கள்.',
        'landing.hero.bookDemo': 'இலவச டெமோ புக் செய்',
        'landing.hero.startTrial': '14 நாள் சோதனை தொடங்கு',
        'landing.hero.stat1': 'பணியாளர்கள் நிர்வகிக்கப்படுகின்றனர்',
        'landing.hero.stat2': 'நிறுவனங்கள் நம்புகின்றன',
        'landing.hero.stat3': 'இயக்க நேர உத்தரவாதம்',
        'landing.hero.stat4': 'மாதம் சேமிப்பு',
        
        // Landing Page - Benefits
        'landing.benefits.compliance': '100% மலேசிய இணக்கம்',
        'landing.benefits.cloud': 'கிளவுட் அணுகல்',
        'landing.benefits.security': 'நிறுவன பாதுகாப்பு',
        'landing.benefits.ai': 'AI தானியங்கி',
        'landing.benefits.setup': '5 நிமிட அமைப்பு',
        'landing.benefits.support': '24/7 ஆதரவு',
        
        // Landing Page - Features
        'landing.features.badge': 'AI அம்சங்கள்',
        'landing.features.title': 'புத்திசாலி HR தானியங்கி',
        'landing.features.subtitle': 'சிக்கலான HR செயல்முறைகளை தானியங்கு செய்ய, கைமுறை பிழைகளை குறைக்க மற்றும் தரவு அடிப்படையிலான முடிவுகளை எடுக்க AI மற்றும் இயந்திர கற்றலைப் பயன்படுத்துங்கள்.',
        'landing.features.payroll': 'AI சம்பள மேலாண்மை',
        'landing.features.payrollDesc': 'சட்டப்பூர்வ இணக்கத்துடன் புத்திசாலி சம்பள தானியங்கி. EPF, SOCSO, EIS & PCB தானியங்கு கணக்கீடு.',
        'landing.features.leave': 'புத்திசாலி விடுப்பு மேலாண்மை',
        'landing.features.leaveDesc': 'முன்கணிப்பு விடுப்பு பகுப்பாய்வு மற்றும் தானியங்கு ஒப்புதல். AI சிறந்த விடுப்பு அட்டவணைகளை பரிந்துரைக்கிறது.',
        'landing.features.attendance': 'புத்திசாலி வருகை',
        'landing.features.attendanceDesc': 'நிகழ்நேர வருகை கண்காணிப்பு. புவி வேலி மற்றும் பயோமெட்ரிக் தயார்.',
        'landing.features.performance': 'செயல்திறன் பகுப்பாய்வு',
        'landing.features.performanceDesc': 'தரவு அடிப்படையிலான செயல்திறன் மதிப்பீடுகள் AI நுண்ணறிவுகளுடன். KPI, இலக்குகள் கண்காணிப்பு.',
        'landing.features.reports': 'முன்கணிப்பு அறிக்கைகள்',
        'landing.features.reportsDesc': 'மேம்பட்ட HR பகுப்பாய்வு. போக்குகளை காட்சிப்படுத்து, குறைவை முன்கணி.',
        'landing.features.complianceEngine': 'இணக்க இயந்திரம்',
        'landing.features.complianceEngineDesc': '100% மலேசிய சட்டப்பூர்வ இணக்கம். EA படிவங்கள், LHDN சமர்ப்பிப்புகள் தானியங்கு உருவாக்கம்.',
        
        // Landing Page - CTA
        'landing.cta.title': 'இன்றே இலவச சோதனை தொடங்கு',
        'landing.cta.subtitle': 'ஏற்கனவே MyHR Orbit பயன்படுத்தும் 500+ மலேசிய நிறுவனங்களுடன் இணையுங்கள்.',
        'landing.cta.note': 'கிரெடிட் கார்டு தேவையில்லை. 5 நிமிடத்தில் அமைக்கவும்.',
        'landing.cta.scheduleDemo': 'டெமோ திட்டமிடு',
        'landing.cta.startTrial': 'இலவச சோதனை தொடங்கு',
        'landing.cta.badge': 'உங்கள் HR-ஐ மாற்ற தயாரா?',
        
        // Landing Page - AI Showcase
        'landing.aiShowcase.badge': '✨ புதியது: AI உந்துதல் அம்சங்கள்',
        'landing.aiShowcase.title': 'உங்கள் AI HR உதவியாளரை சந்தியுங்கள்',
        'landing.aiShowcase.subtitle': 'எங்கள் நவீன AI அம்சங்களுடன் HR-ன் எதிர்காலத்தை அனுபவியுங்கள். சலிப்பான பணிகளை தானியங்கு செய்யுங்கள், உடனடி நுண்ணறிவுகளைப் பெறுங்கள்.',
        'landing.aiShowcase.chatbot': 'AI HR சாட்போட்',
        'landing.aiShowcase.chatbotDesc': 'HR கேள்விகளுக்கு 24/7 உடனடி பதில்களைப் பெறுங்கள். எங்கள் AI உதவியாளர் மலேசிய வேலைவாய்ப்பு சட்டங்கள், EPF, SOCSO மற்றும் நிறுவன கொள்கைகளை அறிந்துள்ளார்.',
        'landing.aiShowcase.scanner': 'ஸ்மார்ட் ரசீது ஸ்கேனர்',
        'landing.aiShowcase.scannerDesc': 'எந்த ரசீதையும் புகைப்படம் எடுத்து AI அனைத்து விவரங்களையும் தானாக பிரித்தெடுக்கட்டும். செலவு கோரிக்கைகளுக்கு கைமுறை தரவு உள்ளீடு இல்லை.',
        'landing.aiShowcase.insights': 'செயல்திறன் நுண்ணறிவுகள்',
        'landing.aiShowcase.insightsDesc': 'ஒரு கிளிக்கில் விரிவான செயல்திறன் பகுப்பாய்வை உருவாக்குங்கள். AI பலங்கள், மேம்பாட்டு பகுதிகளை கண்டறிந்து இலக்குகளை பரிந்துரைக்கிறது.',
        'landing.aiShowcase.tryFree': 'AI அம்சங்களை இலவசமாக முயற்சிக்கவும்',
        'landing.aiShowcase.liveNow': 'நேரடி',
        'landing.aiShowcase.ocrPowered': 'OCR உந்துதல்',
        'landing.aiShowcase.aiAnalytics': 'AI பகுப்பாய்வு',
        
        // Landing Page - Modules
        'landing.modules.badge': 'முழுமையான HR தொகுப்பு',
        'landing.modules.title': 'உங்களுக்கு தேவையான அனைத்தும், ஒரே இடத்தில்',
        'landing.modules.subtitle': 'ஆன்போர்டிங் முதல் ஆஃப்போர்டிங் வரை, MyHR Orbit புத்திசாலி தானியங்கியுடன் மனித வள மேலாண்மையின் ஒவ்வொரு அம்சத்தையும் உள்ளடக்குகிறது.',
        'landing.modules.employees': 'பணியாளர் மேலாண்மை',
        'landing.modules.employeesFeatures': 'முழுமையான பணியாளர் சுயவிவரங்கள் & ஆவணங்கள்|ஆன்போர்டிங் & ஆஃப்போர்டிங் தானியங்கி|அமைப்பு படிநிலை காட்சிப்படுத்தல்|தனிப்பயன் புலங்கள் & தரவு மேலாண்மை',
        'landing.modules.payroll': 'சம்பள செயலாக்கம்',
        'landing.modules.payrollFeatures': 'தானியங்கு சம்பள கணக்கீடுகள்|EPF, SOCSO, EIS, PCB இணக்கம்|வங்கி கோப்பு உருவாக்கம் (அனைத்து மலேசிய வங்கிகள்)|சம்பள சீட்டு உருவாக்கம் & விநியோகம்',
        'landing.modules.leave': 'விடுப்பு & வருகை',
        'landing.modules.leaveFeatures': 'பல விடுப்பு வகை கட்டமைப்புகள்|தானியங்கு விடுப்பு இருப்பு கணக்கீடுகள்|நேர கண்காணிப்பு & மேலதிக நேர மேலாண்மை|மொபைல் செக்-இன்/செக்-அவுட்',
        'landing.modules.claims': 'உரிமைகோரல்கள் & செலவுகள்',
        'landing.modules.claimsFeatures': 'டிஜிட்டல் ரசீது பதிவேற்றங்கள்|பல-நிலை ஒப்புதல் பணிப்பாய்வுகள்|செலவு கொள்கை அமலாக்கம்|சம்பளத்துடன் ஒருங்கிணைப்பு',
        'landing.modules.performance': 'செயல்திறன் மதிப்பாய்வுகள்',
        'landing.modules.performanceFeatures': 'இலக்கு அமைப்பு & கண்காணிப்பு|360-டிகிரி கருத்து|திறன் மதிப்பீடுகள்|AI உந்துதல் செயல்திறன் நுண்ணறிவுகள்',
        'landing.modules.reports': 'அறிக்கைகள் & இணக்கம்',
        'landing.modules.reportsFeatures': '50+ முன்-கட்டமைக்கப்பட்ட அறிக்கை வார்ப்புருக்கள்|தனிப்பயன் அறிக்கை கட்டமைப்பாளர்|EA படிவம் & சட்டப்பூர்வ அறிக்கைகள்|Excel, PDF, CSV-க்கு ஏற்றுமதி',
        
        // Landing Page - Compliance
        'landing.compliance.badge': 'மலேசிய சட்டப்பூர்வ இணக்கம்',
        'landing.compliance.title': 'மலேசிய வணிகங்களுக்காக கட்டப்பட்டது',
        'landing.compliance.subtitle': 'அனைத்து மலேசிய வேலைவாய்ப்பு சட்டங்கள் மற்றும் சட்டப்பூர்வ தேவைகளுக்கு 100% இணக்கமானது. தானியங்கு புதுப்பிப்புகளுடன் கவலையின்றி இருங்கள்.',
        'landing.compliance.epf': 'EPF/KWSP',
        'landing.compliance.epfDesc': 'அனைத்து பங்களிப்பு வகைகளுக்கும் தானியங்கு கணக்கீடுகள்',
        'landing.compliance.socso': 'SOCSO/PERKESO',
        'landing.compliance.socsoDesc': 'வேலை காயம் & செல்லாமை திட்ட கணக்கீடுகள்',
        'landing.compliance.eis': 'EIS/SIP',
        'landing.compliance.eisDesc': 'வேலைவாய்ப்பு காப்பீட்டு அமைப்பு பிடித்தங்கள்',
        'landing.compliance.pcb': 'PCB/MTD',
        'landing.compliance.pcbDesc': 'சமீபத்திய LHDN விகிதங்களைப் பயன்படுத்தி மாதாந்திர வரி பிடித்தம்',
        
        // Landing Page - Forms
        'landing.forms.badge': 'ஒரு கிளிக் பதிவிறக்கங்கள்',
        'landing.forms.title': 'மலேசிய அரசு படிவங்கள்',
        'landing.forms.subtitle': 'ஒரு கிளிக்கில் இணக்கமான சட்டப்பூர்வ படிவங்களை உருவாக்குங்கள். அனைத்து படிவங்களும் உங்கள் நிறுவன மற்றும் பணியாளர் தரவுகளுடன் தானாக நிரப்பப்படும்.',
        
        // Landing Page - Comparison
        'landing.comparison.badge': 'ஏன் எங்களை தேர்வு செய்ய வேண்டும்',
        'landing.comparison.title': 'MyHR Orbit ஏன் புத்திசாலியான தேர்வு',
        'landing.comparison.subtitle': 'மலேசிய சந்தையில் உள்ள மற்ற HRMS தீர்வுகளுடன் நாங்கள் எவ்வாறு ஒப்பிடுகிறோம் என்பதைப் பாருங்கள்.',
        'landing.comparison.feature': 'அம்சம்',
        'landing.comparison.mobileCheckin': 'மொபைல் செக்-இன்/அவுட்',
        'landing.comparison.aiScan': 'AI ஸ்கேனுடன் கோரிக்கைகள்',
        'landing.comparison.bahasaUI': 'பஹாசா மலேசியா UI',
        'landing.comparison.setupTime': 'அமைவு நேரம்',
        'landing.comparison.pricing': 'விலை',
        'landing.comparison.mins': '5 நிமிடங்கள்',
        'landing.comparison.hours': 'மணி/நாட்கள்',
        'landing.comparison.free': 'RM 0',
        'landing.comparison.paid': 'RM 8-15+/பயனர்',
        
        // Landing Page - Testimonials
        'landing.testimonials.badge': 'வெற்றி கதைகள்',
        'landing.testimonials.title': 'மலேசிய வணிகங்களால் நம்பப்படுகிறது',
        
        // Landing Page - Footer
        'landing.footer.description': 'மலேசியாவின் மிகவும் புத்திசாலி HRMS தளம். AI தீர்வுகளுடன் சம்பளத்தை தானியங்கு செய்து 100% சட்டப்பூர்வ இணக்கத்தை உறுதி செய்யுங்கள்.',
        'landing.footer.product': 'தயாரிப்பு',
        'landing.footer.features': 'அம்சங்கள்',
        'landing.footer.pricing': 'விலை',
        'landing.footer.integrations': 'ஒருங்கிணைப்புகள்',
        'landing.footer.api': 'API ஆவணம்',
        'landing.footer.company': 'நிறுவனம்',
        'landing.footer.about': 'எங்களை பற்றி',
        'landing.footer.careers': 'வேலைவாய்ப்புகள்',
        'landing.footer.privacy': 'தனியுரிமை கொள்கை',
        'landing.footer.terms': 'சேவை விதிமுறைகள்',
        'landing.footer.copyright': '© 2026 MyHR Orbit. அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டவை.',
        'landing.footer.madeIn': 'மலேசியாவில் ❤️ உடன் தயாரிக்கப்பட்டது',
        
        // Login Page
        'login.title': 'முழுமையான HR மேலாண்மை தீர்வு',
        'login.subtitle': 'சம்பளம், விடுப்பு, வருகை மற்றும் செயல்திறன் மேலாண்மையை எளிதாக்குங்கள்.',
        'login.compliance': 'EPF, SOCSO, EIS & PCB இணக்கம்',
        'login.welcome': 'மீண்டும் வருக',
        'login.signInTo': 'உங்கள் கணக்கில் உள்நுழையவும்',
        'login.email': 'மின்னஞ்சல்',
        'login.password': 'கடவுச்சொல்',
        'login.signIn': 'உள்நுழை',
        'login.noAccount': 'கணக்கு இல்லையா?',
        'login.createAccount': 'நிறுவன கணக்கு உருவாக்கு',
        'login.platformOwner': 'தளம் உரிமையாளர் அணுகல்',
        'login.platformOwnerDesc': 'அனைத்து நிறுவனங்களையும் அணுகவும், தளத்தை நிர்வகிக்கவும்',
        'login.demoCredentials': 'டெமோ நற்சான்றிதழ்கள்',
        'login.hrManager': 'HR மேலாளர்',
        'login.employee': 'பணியாளர்',
        'login.anyImported': 'இறக்குமதி செய்யப்பட்ட பணியாளர்',

        // Navigation
        'nav.dashboard': 'டாஷ்போர்டு',
        'nav.employees': 'பணியாளர்கள்',
        'nav.myProfile': 'என் சுயவிவரம்',
        'nav.leave': 'விடுப்பு',
        'nav.attendance': 'வருகை',
        'nav.claims': 'உரிமைகோரல்கள்',
        'nav.payroll': 'சம்பளம்',
        'nav.payslips': 'சம்பள சீட்டுகள்',
        'nav.overtime': 'கூடுதல் நேரம்',
        'nav.probation': 'பரிசோதனை காலம்',
        'nav.performance': 'செயல்திறன்',
        'nav.documents': 'ஆவணங்கள்',
        'nav.reports': 'அறிக்கைகள்',
        'nav.forms': 'படிவங்கள்',
        'nav.hrInbox': 'HR இன்பாக்ஸ்',
        'nav.settings': 'அமைப்புகள்',
        'nav.logout': 'வெளியேறு',
        'nav.import': 'தரவை இறக்குமதி',
        'nav.migration': 'தரவு இடம்பெயர்வு',
        'nav.aiConfig': 'AI கட்டமைப்பு',
        'nav.licenseSupport': 'உரிமம் & ஆதரவு',
        'nav.psclAdmin': 'PSCL நிர்வாகம்',
        'nav.platformAdmin': 'தள நிர்வாகம்',
        'nav.myTeam': 'என் குழு',
        
        // Common
        'common.search': 'தேடு',
        'common.filter': 'வடிகட்டு',
        'common.save': 'சேமி',
        'common.cancel': 'ரத்து செய்',
        'common.edit': 'திருத்து',
        'common.delete': 'நீக்கு',
        'common.download': 'பதிவிறக்கு',
        'common.submit': 'சமர்ப்பி',
        'common.approve': 'ஒப்புதல்',
        'common.reject': 'நிராகரி',
        'common.pending': 'நிலுவையில்',
        'common.approved': 'ஒப்புதல் அளிக்கப்பட்டது',
        'common.rejected': 'நிராகரிக்கப்பட்டது',
        'common.loading': 'ஏற்றுகிறது...',
        'common.actions': 'செயல்கள்',
        'common.status': 'நிலை',
        'common.date': 'தேதி',
        'common.name': 'பெயர்',
        'common.email': 'மின்னஞ்சல்',
        'common.phone': 'தொலைபேசி',
        'common.department': 'துறை',
        'common.position': 'பதவி',
        'common.all': 'அனைத்தும்',
        'common.active': 'செயலில்',
        'common.inactive': 'செயலற்றது',
        'common.view': 'பார்',
        'common.add': 'சேர்',
        'common.update': 'புதுப்பி',
        'common.close': 'மூடு',
        'common.yes': 'ஆம்',
        'common.no': 'இல்லை',
        'common.confirm': 'உறுதிப்படுத்து',
        'common.back': 'பின்னால்',
        'common.next': 'அடுத்து',
        'common.previous': 'முந்தைய',
        'common.noData': 'தரவு இல்லை',
        'common.clickToView': 'விவரங்களைப் பார்க்க கிளிக் செய்யவும்',
        
        // Dashboard
        'dashboard.title': 'டாஷ்போர்டு',
        'dashboard.welcome': 'மீண்டும் வருக',
        'dashboard.subtitle': 'இன்று உங்கள் நிறுவனத்தில் நடப்பது இதுதான்.',
        'dashboard.totalEmployees': 'மொத்த பணியாளர்கள்',
        'dashboard.pendingLeaves': 'நிலுவை விடுப்புகள்',
        'dashboard.pendingClaims': 'நிலுவை உரிமைகோரல்கள்',
        'dashboard.monthlyPayroll': 'மாத சம்பளம்',
        'dashboard.employeeGrowth': 'பணியாளர் வளர்ச்சி',
        'dashboard.departmentDistribution': 'துறை விநியோகம்',
        'dashboard.recentActivity': 'சமீபத்திய செயல்பாடு',
        'dashboard.quickActions': 'விரைவு செயல்கள்',
        'dashboard.upcomingLeaves': 'வரவிருக்கும் விடுப்புகள்',
        'dashboard.pendingApprovals': 'நிலுவை ஒப்புதல்கள்',
        'dashboard.addEmployee': 'பணியாளர் சேர்க்க',
        'dashboard.processPayroll': 'சம்பளம் செயலாக்க',
        'dashboard.reviewLeaves': 'விடுப்புகள் மதிப்பாய்வு',
        'dashboard.viewReports': 'அறிக்கைகள் பார்க்க',
        
        // AI Features
        'ai.chatbot': 'AI HR உதவியாளர்',
        'ai.askQuestion': 'கேள்வி கேளுங்கள்...',
        'ai.thinking': 'சிந்திக்கிறது...',
        'ai.quickQuestions': 'விரைவு கேள்விகள்',
        'ai.receiptScanner': 'AI ரசீது ஸ்கேனர்',
        'ai.scanningReceipt': 'ரசீது ஸ்கேன் செய்கிறது...',
        'ai.performanceInsights': 'AI செயல்திறன் நுண்ணறிவு',
        'ai.generatingInsights': 'நுண்ணறிவு உருவாக்குகிறது...',
        
        // Auth
        'auth.login': 'உள்நுழை',
        'auth.logout': 'வெளியேறு',
        'auth.email': 'மின்னஞ்சல்',
        'auth.password': 'கடவுச்சொல்',
        'auth.forgotPassword': 'கடவுச்சொல் மறந்துவிட்டதா?',
        'auth.signIn': 'உள்நுழை',
        'auth.signUp': 'பதிவு செய்',
        'auth.rememberMe': 'என்னை நினைவில் கொள்',
      }
    };

    return translations[language]?.[key] || translations['en']?.[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, toggleLanguage, getCurrentLanguage, t, languages }}>
      {children}
    </LanguageContext.Provider>
  );
};
